import { useState } from 'react';
import {
  Drawer,
  Typography,
  Stack,
  Button,
  useTheme,
  alpha,
  Divider,
  CircularProgress,
  DialogActions,
  Box,
} from '@mui/material';
import { Send } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
  FormProvider,
  RHFTextField,
  RHFTextArea,
  RHFAutocomplete,
  RHFUpload,
  enqueueSnackbar,
  useUserDetailsStore,
} from '@zeta-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  SupportTicketService,
  ICreateSupportTicketRequest,
  DocumentService,
} from '@zeta-services';

interface ICreateTicketForm {
  subject: string;
  priority: string | { label: string; value: string };
  description: string;
  attachments?: File[];
}

interface FloatingSupportFormProps {
  open: boolean;
  onClose: () => void;
}

const FloatingSupportForm = ({ open, onClose }: FloatingSupportFormProps) => {
  const theme = useTheme();
  const user = useUserDetailsStore((state) => state.user);
  const currentUserId = user?.id;
  const queryClient = useQueryClient();
  const [isUploading, setIsUploading] = useState(false);

  const validationSchema = Yup.object().shape({
    subject: Yup.string()
      .required('Subject is required')
      .test('not-empty', 'Subject cannot be empty', (value) => value?.trim() !== ''),
    description: Yup.string()
      .required('Description is required')
      .test('not-empty', 'Description cannot be empty', (value) => value?.trim() !== ''),
    priority: Yup.mixed().nullable(),
    attachments: Yup.mixed().nullable(),
  });

  const methods = useForm<ICreateTicketForm>({
    defaultValues: {
      subject: '',
      priority: '',
      description: '',
      attachments: [],
    },
    resolver: yupResolver(validationSchema),
    mode: 'onSubmit',
  });

  const priorityOptions = [
    { label: 'Urgent', value: 'Urgent' },
    { label: 'High', value: 'High' },
    { label: 'Medium', value: 'Medium' },
    { label: 'Low', value: 'Low' },
  ];

  // Create ticket mutation
  const createTicketMutation = useMutation({
    mutationFn: (payload: ICreateSupportTicketRequest) =>
      SupportTicketService().createTicket(payload),
    onSuccess: (response) => {
      enqueueSnackbar(
        response.data?.message || 'Support ticket created successfully!',
        { variant: 'success' }
      );
      methods.reset();
      onClose();
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
    },
    onError: () => {
      enqueueSnackbar('Failed to create support ticket. Please try again.', {
        variant: 'error',
      });
    },
  });

  const handleCreateTicket = async (data: ICreateTicketForm) => {
    const priorityValue =
      typeof data.priority === 'string'
        ? data.priority
        : (data.priority as { label: string; value: string })?.value || '';

    let attachmentIDs: string[] | null = null;

    // Upload documents first if attachments exist
    if (data.attachments && data.attachments.length > 0) {
      setIsUploading(true);
      try {
        const formData = new FormData();
        data.attachments.forEach((file) => {
          formData.append('files', file);
        });
        formData.append('documentType', 'Support Request');
        formData.append('creatorKey', currentUserId);

        const uploadResponse = await DocumentService().createBatchDocuments(
          formData
        );
        const uploadData = uploadResponse.data as any;

        // Extract document keys from the batch upload response
        let results: any[] = [];
        if (uploadData?.resp?.results) {
          results = uploadData.resp.results;
        } else if (uploadData?.data?.resp?.results) {
          results = uploadData.data.resp.results;
        } else if (uploadData?.results) {
          results = uploadData.results;
        }

        // Filter successful uploads and extract documentKeys
        const successfulUploads = results.filter(
          (result: any) => result.success === true && result.documentKey
        );
        if (successfulUploads.length === 0) {
          enqueueSnackbar(
            'Failed to upload attachments. Please try again.',
            { variant: 'error' }
          );
          setIsUploading(false);
          return;
        }

        attachmentIDs = successfulUploads.map(
          (result: any) => result.documentKey
        );

        // Warn if some uploads failed
        const failedUploads = results.filter(
          (result: any) => result.success !== true
        );
        if (failedUploads.length > 0) {
          enqueueSnackbar(`${failedUploads.length} file(s) failed to upload`, {
            variant: 'warning',
          });
        }
      } catch (error) {
        enqueueSnackbar('Failed to upload attachments. Please try again.', {
          variant: 'error',
        });
        setIsUploading(false);
        return;
      } finally {
        setIsUploading(false);
      }
    }

    const payload: ICreateSupportTicketRequest = {
      subject: data.subject,
      description: data.description,
      priority: priorityValue,
      attachmentsIDs: attachmentIDs,
    };
    createTicketMutation.mutate(payload);
  };

  const handleClose = () => {
    if (!createTicketMutation.isPending && !isUploading) {
      methods.reset();
      onClose();
    }
  };

  const isSubmitting = createTicketMutation.isPending || isUploading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: '420px',
          background: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column',
        },
        '& .MuiModal-backdrop': {
          background: alpha(theme.palette.common.black, 0.48),
        },
      }}
    >
      <FormProvider methods={methods} onSubmit={methods.handleSubmit(handleCreateTicket)}>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 3,
            pb: 10,
          }}
        >
          <Stack spacing={2.5}>
            <Stack spacing={0.5}>
              <Typography variant="h6">Report an Issue</Typography>
              <Typography variant="caption" color="text.secondary">
                Describe your issue and our support team will get back to you
              </Typography>
            </Stack>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}
              >
                Describe your issue <span style={{ color: 'red' }}>*</span>
              </Typography>
              <RHFTextField
                name="subject"
                placeholder="Brief description of your issue"
                size="small"
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}
              >
                Priority
              </Typography>
              <RHFAutocomplete
                name="priority"
                placeholder="Select priority level"
                size="small"
                options={priorityOptions}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}
              >
                Additional Details <span style={{ color: 'red' }}>*</span>
              </Typography>
              <RHFTextArea
                name="description"
                placeholder="Provide detailed information about your issue"
                minRows={2}
              />
            </Box>

            <Divider sx={{ my: 0.5 }} />

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 0.5, fontWeight: 600, color: 'text.primary', fontSize: '0.875rem' }}
              >
                Attach Screenshots (Optional)
              </Typography>
              <RHFUpload
                name="attachments"
                multiple
                maxSize={10485760}
                paste={true}
                header="Click to browse or drag and drop"
                subtitle="PNG, JPG up to 10MB"
              />
            </Box>
          </Stack>
        </Box>

        <DialogActions
          sx={{
            position: 'sticky',
            bottom: 0,
            px: 3,
            py: 2,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            background: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            zIndex: 10,
          }}
        >
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={isSubmitting}
            startIcon={
              isSubmitting ? <CircularProgress size={14} color="inherit" /> : <Send size={14} />
            }
          >
            {isSubmitting ? 'Submitting...' : 'Submit Report'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Drawer>
  );
};

export default FloatingSupportForm;
